import { Injectable } from '@angular/core';
import { OrgCapabilityAction } from '@configs/constants';
import { OrganizationCapabilityPlan } from '@core/models/organization-capability-plan.model';
import { OrganizationEntitlement } from '@core/models/organization-entitlement.model';
import { SharedStateModel } from '@core/models/shared-state.model';
import { OrgService } from '@core/services/org.service';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { NavigationConfig, Organization } from 'app/modules/organizations-manager/models/organization.model';
import { map, tap } from 'rxjs';
import {
  FetchOrganization,
  LoadOrganizationEntitlementsCapabilityData,
  ResetSharedState,
  SetOrganization,
  SetOrganizationEntitlementsCapabilityData,
  SetSpinner,
} from './shared.actions';

const defaults: SharedStateModel = {
  spinner: false,
  country: null,
  organization: null,
  organizationEntitlements: {},
  organizationCapabilityPlan: null,
  spinnerMessage: '',
};

@State<SharedStateModel>({
  name: 'shared',
  defaults,
})
@Injectable()
export class SharedState {
  constructor(private readonly orgService: OrgService) {}

  @Selector()
  static getSpinnerStatus(state: SharedStateModel): { spinner: boolean; spinnerMessage: string } {
    return { spinner: state.spinner, spinnerMessage: state.spinnerMessage };
  }

  @Selector()
  static getNavigationConfig(state: SharedStateModel): NavigationConfig[] {
    return state.organization?.orgConfig?.navigationConfig || [];
  }

  @Selector()
  static getOrganization(state: SharedStateModel): Organization {
    return state.organization;
  }

  @Selector()
  static isControlTowerEnabledForTheOrg(state: SharedStateModel): boolean {
    return state.organization?.controlTowerEnabled || false;
  }

  @Selector()
  static hasOrgCapability(state: SharedStateModel): (capabilityKey: string) => boolean {
    return (capabilityKey: string) => {
      const capability = state.organizationEntitlements[capabilityKey];
      return capability != null && capability.enabled;
    };
  }

  @Selector()
  static hasOrgCapabilityPermission(
    state: SharedStateModel
  ): (capabilityKey: string, permission: OrgCapabilityAction) => boolean {
    return (capabilityKey: string, permission: OrgCapabilityAction) => {
      const capability = state.organizationEntitlements[capabilityKey];
      return capability != null && capability.enabled && capability[`${permission}`];
    };
  }

  @Selector()
  static getOrgCapability(state: SharedStateModel): (capabilityKey: string) => OrganizationEntitlement {
    return (capabilityKey: string) => {
      return state.organizationEntitlements[capabilityKey];
    };
  }

  @Selector()
  static getOrgCapabilities(state: SharedStateModel): Record<string, OrganizationEntitlement> {
    return state.organizationEntitlements;
  }

  @Selector()
  static getOrganizationEntitlements(state: SharedStateModel): Record<string, OrganizationEntitlement> {
    return state.organizationEntitlements;
  }

  @Selector()
  static getOrganizationCapabilityPlan(state: SharedStateModel): OrganizationCapabilityPlan {
    return state.organizationCapabilityPlan;
  }

  @Action(SetSpinner)
  setSpinner({ patchState }: StateContext<SharedStateModel>, { isEnable, message }: SetSpinner) {
    return patchState({ spinner: isEnable, spinnerMessage: message });
  }

  @Action(ResetSharedState)
  resetState({ setState }: StateContext<SharedStateModel>) {
    return setState(defaults);
  }

  @Action(SetOrganization)
  setOrganization({ patchState }: StateContext<SharedStateModel>, { organization }: SetOrganization) {
    return patchState({ organization });
  }

  @Action(FetchOrganization)
  fetchOrganization({ getState, patchState }: StateContext<SharedStateModel>, { orgId }: FetchOrganization) {
    const { organization } = getState();
    return this.orgService.getOrgById(orgId).pipe(
      tap(({ controlTowerEnabled, landingPageUrl }) => {
        patchState({ organization: { ...(organization && organization), controlTowerEnabled, landingPageUrl } });
      })
    );
  }

  @Action(LoadOrganizationEntitlementsCapabilityData)
  loadOrganizationEntitlements(
    { patchState }: StateContext<SharedStateModel>,
    { orgId }: LoadOrganizationEntitlementsCapabilityData
  ) {
    return this.orgService.getOrganizationEntitlementsCapabilityPlan(orgId).pipe(
      map(({ orgEntitlements, organizationCapabilityPlanByOrgId }) => {
        const organizationEntitlements = orgEntitlements.reduce((acc, entitlement) => {
          acc[entitlement.capabilityKey] = entitlement;
          return acc;
        }, {});
        return patchState({ organizationEntitlements, organizationCapabilityPlan: organizationCapabilityPlanByOrgId });
      })
    );
  }

  @Action(SetOrganizationEntitlementsCapabilityData)
  setOrganizationEntitlementsCapabilityData(
    { patchState }: StateContext<SharedStateModel>,
    { orgEntitlements, organizationCapabilityPlan }: SetOrganizationEntitlementsCapabilityData
  ) {
    const organizationEntitlements = orgEntitlements.reduce((acc, entitlement) => {
      acc[entitlement.capabilityKey] = entitlement;
      return acc;
    }, {});
    return patchState({ organizationEntitlements, organizationCapabilityPlan });
  }
}
